import React, { memo, useEffect, useMemo } from 'react';
import { futuresApi } from '../../../Apis/Apis';
import {
    Currency,
    DemeterDataFrequency,
    DemeterDataSource,
    DemeterFilterTimeSpan,
    DemeterSymbolModel,
    MarketPricesTimeSpan,
    UnitOfMeasure,
} from '../../../Generated/Raven-Demeter';
import useApi from '../../Apis/Hooks/useApiHook';
import useSymbolsApi from '../../Apis/Hooks/useSymbolsApiHook';
import { ChartDisplayType, IChartDataSeries } from '../../Components/Charts/ChartDefinitions';
import ChartWrapper from '../../Components/Charts/ChartWrapper/ChartWrapper';
import PriceChartRaw from '../../Components/Charts/Price/PriceChartRaw';
import formattingService from '../../Services/Formatting/FormattingService';
import styles from './MarketIntelligenceReports.module.scss';

const filterTimeSpan = DemeterFilterTimeSpan.All;
const timeSpan = MarketPricesTimeSpan.All;
const defalutDemeterPriceFrequency = DemeterDataFrequency.Daily;
const defalutDisplayType = ChartDisplayType.Line;

interface IMarketIntelligenceReportsChartProps {
    reutersInstrumentCode: string;
}

const MarketIntelligenceReportsChart: React.FC<IMarketIntelligenceReportsChartProps> = (props: IMarketIntelligenceReportsChartProps) => {
    // Aplication hooks.
    const symbols = useSymbolsApi();

    // Api hooks.
    const [, refreshListMarketPricesResponse, listMarketPricesResponse] = useApi(() => futuresApi.listMarketPrices(props.reutersInstrumentCode!, timeSpan), {
        stopAutoExecute: true,
    });

    const selectedSymbols = useMemo(() => {
        if (!listMarketPricesResponse) {
            return {} as DemeterSymbolModel;
        }

        return symbols?.find((y) =>
            y.symbolContracts.map((z) => z?.reutersInstrumentCode).includes(listMarketPricesResponse.rows![0]?.reutersInstrumentCode ?? ''),
        );
    }, [symbols, listMarketPricesResponse]);

    const linesSeries = useMemo(() => {
        if (!listMarketPricesResponse || listMarketPricesResponse?.rows?.length === 0 || !symbols || !selectedSymbols) {
            return [];
        }

        return [
            {
                label: selectedSymbols?.symbolContracts.find((x) => x.reutersInstrumentCode === props.reutersInstrumentCode)?.name,
                data: listMarketPricesResponse?.rows?.map((row) => ({
                    value: row.settlementPrice,
                    asOfDate: new Date(row.asOfDate),
                    isActualValue: true,
                })),
            },
        ];
    }, [listMarketPricesResponse, selectedSymbols]) as IChartDataSeries[];

    useEffect(() => {
        if (!symbols || listMarketPricesResponse) {
            return;
        }

        refreshListMarketPricesResponse();
    }, [symbols]);

    const timestamp = `${formattingService.toTimestamp(new Date())} ${formattingService.getTimeZone()}`;

    const title = useMemo(() => {
        if (!selectedSymbols?.displayName) {
            return '';
        }

        return `${selectedSymbols?.exchange} ${selectedSymbols?.displayName}`;
    }, [selectedSymbols]);
    const currency = useMemo(() => {
        if (!listMarketPricesResponse) {
            return '' as Currency;
        }

        return symbols?.find((y) => y.symbolContracts.map((z) => z.reutersInstrumentCode).includes(listMarketPricesResponse?.rows![0]?.reutersInstrumentCode))
            ?.currency;
    }, [symbols, listMarketPricesResponse]);

    const unitOfMeasure = useMemo(() => {
        if (!listMarketPricesResponse) {
            return '' as UnitOfMeasure;
        }

        return symbols?.find((y) => y.symbolContracts.map((z) => z.reutersInstrumentCode).includes(listMarketPricesResponse?.rows![0]?.reutersInstrumentCode))
            ?.unitOfMeasure;
    }, [symbols, listMarketPricesResponse]);

    const dataSourceTag = useMemo(() => {
        if (!listMarketPricesResponse) {
            return '' as DemeterDataSource;
        }

        return symbols?.find((y) => y.symbolContracts.map((z) => z.reutersInstrumentCode).includes(listMarketPricesResponse?.rows![0]?.reutersInstrumentCode))
            ?.dataSourceTag;
    }, [symbols, listMarketPricesResponse]);

    return (
        <div className={styles.market_intelligence_reports_chart}>
            <ChartWrapper
                name="MarketIntelligenceReportsChart"
                headerOptions={{
                    hideDownload: true,
                    hidePopoutModal: true,
                }}
                title={title}
                subtitle={timestamp}
                dataSourceTag={dataSourceTag!}
                isLoading={linesSeries.length === 0}
                testId={`MarketIntelligenceReportsChart_${props.reutersInstrumentCode}`}
                track={{ eventCategory: 'MarketIntelligenceReports', eventAction: 'MarketIntelligenceReportsChart' }}
            >
                <PriceChartRaw
                    displayType={defalutDisplayType}
                    linesSeries={linesSeries}
                    dataFrequency={defalutDemeterPriceFrequency}
                    currency={currency}
                    unitOfMeasure={unitOfMeasure}
                    filterTimeSpan={filterTimeSpan}
                    hidePriceNavigator
                    displayDecimalPlacesMinimum={0}
                    displayDecimalPlacesMaximum={4}
                />
            </ChartWrapper>
        </div>
    );
};

export default memo(MarketIntelligenceReportsChart);
